import { Time } from "@internationalized/date"
import {
  Box,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  Form,
  FormDateInput,
  FormInput,
  useAlert,
} from "@prismicio/editor-ui"
import { type ReactNode, useState } from "react"

import {
  createRelease as doCreateRelease,
  type Release,
  updateReleasesListData,
} from "../../app/services/release"

interface CreateReleaseDialogProps {
  children: ReactNode
  onSuccess?: (release: Release) => void
}

export function CreateReleaseDialog(props: CreateReleaseDialogProps) {
  const { children, onSuccess } = props

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [release, setRelease] = useState<NewRelease>({
    label: "",
    date: undefined,
  })

  const { showAlert } = useAlert()
  const { disabled, reason } = getOkButtonDisabledProperties({
    release,
  })

  function onOkClick() {
    if (!release || disabled) return

    setLoading(true)
    createRelease({
      release,
      onSuccess: (release) => {
        setLoading(false)
        setOpen(false)
        setRelease({ label: "", date: undefined })
        onSuccess?.(release)
      },
      onError: () => {
        setLoading(false)
        setOpen(false)
        showAlert({
          title: `Oops! We were unable to create your release.`,
          subtitle: `We encountered an error while trying to create your release. Please contact our support team if this issue persists.`,
          onRetry: onOkClick,
        })
      },
    }).catch(() => undefined)
  }

  return (
    <Dialog
      trigger={children}
      size="small"
      open={open}
      onOpenChange={setOpen}
      noDescription
    >
      <DialogHeader icon="calendarToday" title="Add Release" />

      <DialogContent>
        <Form onSubmit={onOkClick}>
          <Box
            flexDirection="column"
            padding={{ inline: 16, top: 16, bottom: 4 }}
            flexGrow={1}
            gap={16}
          >
            <FormInput
              value={release.label}
              label="Release name"
              onValueChange={(value) =>
                setRelease({ ...release, label: value })
              }
              placeholder="e.g: Website update"
              disabled={loading}
            />
            <FormDateInput
              mode="date-time"
              label="Schedule publication"
              minValue="today"
              value={release?.date}
              onValueChange={(value) => setRelease({ ...release, date: value })}
              defaultTime={new Time(0)}
              disabled={loading}
            />
          </Box>
        </Form>

        <DialogActions>
          <DialogCancelButton />
          <DialogActionButton
            onClick={onOkClick}
            disabled={disabled}
            tooltipLabel={reason}
            loading={loading}
          >
            Add
          </DialogActionButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}

interface NewRelease {
  label: string
  date?: Date
}

interface GetOkButtonDisabledPropertiesArgs {
  release: NewRelease
}

function getOkButtonDisabledProperties(
  props: GetOkButtonDisabledPropertiesArgs,
) {
  const { release } = props

  if (!release.label) {
    return { disabled: true, reason: "The release name is required" }
  }

  if (release.date && release.date < new Date()) {
    return { disabled: true, reason: "The release date must be in the future" }
  }

  return { disabled: false }
}

interface CreateReleaseArgs {
  release: NewRelease
  onSuccess: (release: Release) => void
  onError: () => void
}

async function createRelease(args: CreateReleaseArgs) {
  const { release, onSuccess, onError } = args

  try {
    const createdRelease = await doCreateRelease(release)
    updateReleasesListData((releases) => [...releases, createdRelease])
    onSuccess(createdRelease)
  } catch (_) {
    onError()
  }
}
